// src/groups — 컨덕터 코드 DS(그룹) 원본. 데브 레포에는 플러그인이 **레포당 1벌**(src/ui-groups/)로 복사하고
// `sync` 가 diff 로 오간다(2026-10-22 결정, DECISIONS). 그래서 이 폴더의 파일은 어느 레포에 떨어져도 그대로 컴파일돼야 한다:
//   허용 import = react · @ant-design/icons · dayjs · @musinsa/pbo-core(+/theme) · @musinsa/pbo-assets(아이콘) · 같은 폴더(./). 컨덕터 내부(@/…)·antd 직접 금지.
//   게이트 = scripts/checks/groups-portable.mjs (check:groups-portable).
// 조회영역 그룹(cmp.FilterCard + cmp.FilterField) — **데모와 dev 가 같이 쓰는 파일 하나**(2026-10-20).
//
// 왜: 종전엔 같은 조회영역을 두 벌로 만들었다 — 데모는 registry/filterControl.tsx(컨덕터 값 저장소에 묶임),
//   dev 산출물은 codegen/emitFilterField.ts 안에 **문자열로 적힌** 보조 컴포넌트를 화면마다 복사해 넣었다.
//   두 벌은 반드시 어긋난다(#798 — 데모 동작 8가지가 산출물에서 빠졌다). 결정(사용자 2026-10-20):
//   그룹은 코드 파일 하나로, 데모도 dev 도 그 파일을 쓴다. 그룹 구조를 바꾸는 건 이 파일을 고치는 일이다.
//
// 이 파일의 규칙
//  · import 는 react · dayjs · @ant-design/icons · @musinsa/pbo-core · @musinsa/pbo-assets 만. 컨덕터 경로(@/…)를 쓰면 dev 레포에서 깨진다.
//    핸드오프는 이 파일을 **그대로**(동반 파일) 싣는다 — codegen/companionSources.ts.
//  · 보조 컴포넌트(FilterField·MultiCodeInput·MultiSelectBox·DateRangeField·칩)는 2026-10-20 시점 산출물에 실제로
//    들어가던 코드를 그대로 옮겼다(emitFilterField.ts HELPERS). 시험(영수증 관리) 뒤 전 화면이 이 파일로 넘어오면
//    문자열 사본과 데모 구현(filterControl.tsx)을 지운다 — 그때까지는 이 파일이 세 번째 사본이다.
//  · 도메인 규칙(성준님 2026-10-23): 데모와 데브를 비교해 **데브에만 있던 손잡이**(allowFuture · allowClear · disabledFields · canReset)는 기본 꺼짐이고
//    그 도메인(지금은 MOMS) 화면만 켠다. 데브에만 있던 **동작**은 여기 두지 않고 domains/<도메인>/ 으로(잠긴 화주유형 동기화).
//  · 값(조회 조건)은 **화면이 소유**한다(value/onChange) — 표 조회가 같은 값을 읽어야 해서다. 접기와 초기화 세대는
//    이 그룹이 소유한다.
import { type ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { CloseCircleFilled, DownOutlined, PlusOutlined, SearchOutlined, UpOutlined } from '@ant-design/icons'
import dayjs, { type Dayjs } from 'dayjs'
import { core } from '@musinsa/pbo-core'

export type FilterOption = { value: string | number; label: string }
export type FilterValues = Record<string, unknown>
export type FilterControl = 'input' | 'code' | 'select' | 'multiSelect' | 'date' | 'radio' | 'pair'

/** 조회 칸 하나 — 데모 그룹 cmp.FilterField 의 인스턴스 속성과 같은 이름이다. */
export type FilterFieldSpec = {
  /** 조회 조건 객체의 키(데모 filterKey, 없으면 노드 id). */
  key: string
  /** 칩을 눌렀을 때 그 칸으로 포커스를 옮기는 표식(노드 id). 없으면 key. */
  fieldId?: string
  control: FilterControl
  label?: string
  required?: boolean
  tooltip?: string
  width?: number | string
  placeholder?: string
  disabled?: boolean
  /** select·multiSelect·radio 선택지. */
  options?: FilterOption[]
  /** select·multiSelect 의 '전체'(값 ''). 미지정 = 필수가 아니면 켬(데모·codegen 공통 규칙). */
  allOption?: boolean
  /** select·radio 기본값 — 조회 조건에도 시딩한다(filterDefaults). */
  defaultValue?: string
  /** code — 다건 입력 한도(기본 1,500). */
  limit?: number
  /** date — 단일 날짜. */
  single?: boolean
  defaultPeriod?: string
  periodBase?: 'futureTo' | 'pastTo'
  /** date — pastTo 인데 미래 날짜도 고를 수 있게(프리셋 '최근 N개월' 은 유지). moms-fe 65a47d0b(RGR12·RGR15) 가 정한 방식 — periodBase 를 지우면 프리셋까지 '오늘부터' 로 바뀐다. */
  allowFuture?: boolean
  maxRangeDays?: number
  hidePresets?: boolean
  /** date — 월 범위(RangePicker picker=month, YY-MM). 값 = [시작월 1일, 종료월 말일]. defaultPeriod 'month3M' = 3개월 전 달 ~ 이번 달. */
  monthRange?: boolean
  /** pair(화주유형 + 화주명) — 두 값 키. 값은 둘 다 문자열 배열(단일 모드도 [v]). */
  typeKey?: string
  nameKey?: string
  typeOptions?: FilterOption[]
  nameOptions?: FilterOption[]
  typeAllOption?: boolean
  nameAllOption?: boolean
  typeMode?: 'single' | 'multi'
  nameMode?: 'single' | 'multi'
  /** pair 단일 화주유형의 기본값 — 조회 조건에도 시딩한다(filterDefaults). */
  typeDefault?: string
  /** input — 돋보기(서치헬퍼 모달 열기). 모달은 화면 소유 — 이 콜백이 연다. (2026-10-23 인라인 helperOpens 미러) */
  onHelper?: () => void
  /** date — 기준일 셀렉트(typeOptions)의 값 키. 있으면 조회 조건에 싣고(typeKey), 없으면 보이기만 한다(인라인 DateRangeField typeOptions 미러). */
  /** pair — 화주유형(single)을 바꾸면 화주명을 비운다 · 옵션에 없는 화주명을 자동 제외(multi) · 유형 지우기(×) 허용(single). 인라인 pair 플래그 미러. */
  typeChangeClearsName?: boolean
  namePruneToOptions?: boolean
  typeAllowClear?: boolean
  /** pair — 화주유형이 '전체'인 동안 화주명 '전체' 를 잠근다(전체×전체 배제). 어댑터·codegen 과 같은 opt-in. */
  nameAllDisabledWhenTypeAll?: boolean
  /** pair — 칸 잠금(권한으로 화주가 고정된 사용자). FilterCard disabledFields[typeKey|nameKey] 로도 준다.
   *  "잠긴 화주유형 값이 옵션을 따라감" 은 MOMS 전용 동작이라 여기 없다 — domains/moms/shipperTypeLock.ts(화면이 건다). */
  typeDisabled?: boolean
  nameDisabled?: boolean
}

const allowAll = (f: FilterFieldSpec): boolean => (typeof f.allOption === 'boolean' ? f.allOption : !f.required)

/** 조회 조건 기본값 — select·radio 의 defaultValue 를 조건에도 넣는다(화면에만 보이고 조회엔 빠지던 사고 moms-fe #758).
 *  기간(DateRangeField)은 마운트 때 스스로 채우므로 여기 없다. 결과는 모듈 상수로 두고 [초기화] 가 이 값으로 돌아간다. */
export function filterDefaults(fields: FilterFieldSpec[]): FilterValues {
  const out: FilterValues = {}
  // forEach — 소비 레포(moms-fe) ESLint 가 for…of 를 막는다(no-restricted-syntax, 2026-10-23 gr01 dry-run 실측). 그룹은 레포로 복사되므로 그 규칙까지 맞춘다.
  fields.forEach((f) => {
    if ((f.control === 'select' || f.control === 'radio') && f.defaultValue != null && f.defaultValue !== '') out[f.key] = f.defaultValue
    // 화주유형 단일 기본값 — 셀렉트 기본값과 같은 규칙(실제 값만, '전체' 제외). 데모 PairControl 이 마운트 때 저장소에 올리던 값.
    if (f.control === 'pair' && f.typeMode === 'single' && f.typeDefault && f.typeKey) out[f.typeKey] = [f.typeDefault]
  })
  return out
}

// ── 보조 컴포넌트 — 산출물(emitFilterField HELPERS)과 같은 코드 ─────────────────────────────

const POPUP_DOWN = {
  bottomLeft: { points: ['tl', 'bl'], offset: [0, 4], overflow: { adjustX: true, adjustY: false } },
  bottomRight: { points: ['tr', 'br'], offset: [0, 4], overflow: { adjustX: true, adjustY: false } },
}

const filterFieldClass = (key: string): string => `pbo-ff--${key.replace(/[^A-Za-z0-9_-]/g, '_')}`
const FILTER_FOCUSABLE = 'input:not([type=hidden]):not([disabled]), textarea:not([disabled]), [contenteditable=true], button:not([disabled])'
const FILTER_OPEN_EVENTS = ['pointerdown', 'mousedown', 'mouseup', 'click'] as const
const openFilterField = (open: () => void, key: string) => {
  open()
  window.setTimeout(() => {
    const frame = document.querySelector(`.${filterFieldClass(key)}`)
    if (!frame) return
    frame.querySelector<HTMLElement>(FILTER_FOCUSABLE)?.focus()
    // 고른 항목을 바로 고칠 수 있게 목록·달력까지 펼친다 — antd 는 포커스만으로 열지 않는다(셀렉트=selector, 날짜=입력이 받는다).
    const host = frame.querySelector<HTMLElement>('.ant-select-selector') ?? frame.querySelector<HTMLElement>('.ant-picker input')
    if (!host) return
    // forEach — dev 레포 ESLint(no-restricted-syntax)가 for…of 를 막는다(2026-09-19 dry 실측).
    FILTER_OPEN_EVENTS.forEach((type) => host.dispatchEvent(new MouseEvent(type, { bubbles: true, cancelable: true, view: window })))
  }, 0)
}

const chipText = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null)
const chipCodes = (v: unknown): string | null => (Array.isArray(v) && v.length ? `${(v as string[])[0]}${v.length > 1 ? ` 외 ${v.length - 1}개` : ''}` : null)
const chipSelect = (opts: FilterOption[], v: unknown, required?: boolean): string | null =>
  v == null || v === '' ? (required ? '전체' : null) : (opts.find((o) => o.value === v)?.label ?? String(v as string | number))
const chipMulti = (opts: FilterOption[], v: unknown, required?: boolean): string | null => {
  const arr = (Array.isArray(v) ? (v as string[]) : []).filter((x) => x !== '')
  return arr.length ? `${opts.find((o) => o.value === arr[0])?.label ?? arr[0]}${arr.length > 1 ? ` 외 ${arr.length - 1}` : ''}` : required ? '전체' : null
}
const chipRange = (v: unknown, fmt = 'YY-MM-DD'): string | null => (Array.isArray(v) && v[0] && v[1] ? `${dayjs(String(v[0])).format(fmt)} ~ ${dayjs(String(v[1])).format(fmt)}` : null)
const chipDate = (v: unknown): string | null => (typeof v === 'string' && v ? dayjs(v).format('YY-MM-DD') : null)

/** 기본 기간 프리셋 → ['YYYY-MM-DD','YYYY-MM-DD']. periodBase 'futureTo' = 오늘~+N(입고예정일 등) / 'pastTo' = -N~오늘. */
const presetRange = (preset?: string, periodBase: 'futureTo' | 'pastTo' = 'futureTo'): [string, string] | undefined => {
  if (!preset) return undefined
  const now = dayjs()
  const n = preset === 'today' ? [0, 'day'] : preset === 'week' ? [6, 'day'] : preset === 'month' ? [1, 'month'] : preset === 'month3' ? [3, 'month'] : preset === 'month6' ? [6, 'month'] : null
  if (!n) return undefined
  const other = periodBase === 'futureTo' ? now.add(n[0] as number, n[1] as 'day' | 'month') : now.subtract(n[0] as number, n[1] as 'day' | 'month')
  const [a, b] = periodBase === 'futureTo' ? [now, other] : [other, now]
  return [a.format('YYYY-MM-DD'), b.format('YYYY-MM-DD')]
}

const toDayjsPair = (v: unknown): [Dayjs, Dayjs] | null => (Array.isArray(v) && v[0] && v[1] ? [dayjs(String(v[0])), dayjs(String(v[1]))] : null)

/** 조회영역 필드 프레임(그룹 cmp.FilterField) — 접힘이면 값 칩(값 없으면 숨김), 열림이면 DS FormItem(라벨 위). 규격 = pbo-core filter-layout.css .pbo-filter-field */
function FilterField({ collapsed, label, required, tooltip, width, chip, fieldKey, onOpen, children }: { collapsed: boolean; label?: string; required?: boolean; tooltip?: string; width?: number | string; chip?: string | null; fieldKey?: string; onOpen?: () => void; children?: ReactNode }) {
  if (collapsed) {
    // 칩을 누르면 조회영역이 열리고 그 항목으로 포커스가 간다(전 화면 공통).
    const open = fieldKey && onOpen ? () => openFilterField(onOpen, fieldKey) : undefined
    return (
      <div
        className="pbo-filter-closed"
        hidden={chip == null}
        {...(open
          ? {
              role: 'button',
              tabIndex: 0,
              style: { cursor: 'pointer' }, // 규격 CSS 는 칩을 읽기 전용으로 본다 — 누를 수 있다는 표시만 여기서
              onClick: open,
              onKeyDown: (e: { key: string; preventDefault: () => void }) => {
                if (e.key !== 'Enter' && e.key !== ' ') return
                e.preventDefault()
                open()
              },
            }
          : {})}
      >
        {label ? (
          <div className="pbo-filter-chip-label">
            <label>{label}</label>
            {required ? <span className="pbo-filter-req">*</span> : null}
          </div>
        ) : null}
        <div className="pbo-filter-chip-value">{chip}</div>
      </div>
    )
  }
  return (
    <core.FormItem className={fieldKey ? `pbo-filter-field ${filterFieldClass(fieldKey)}` : 'pbo-filter-field'} label={label} required={required} tooltip={!!tooltip} tooltipText={tooltip} labelPosition="top" boxedItem={false} style={width != null ? { width } : undefined}>
      {children}
    </core.FormItem>
  )
}

/** 다건 코드 입력 — 인풋 + 우측 [+] 팝오버 textarea(Enter 로 다중 입력, 최대 `limit` — 기본 1,500). 규격 = .pbo-filter-code*
 *  구분자는 인풋·팝오버 모두 쉼표·공백·줄바꿈(1.0 코드 입력 동치 — 같은 문자열이 두 경로에서 다르게 해석되지 않게).
 *  한도는 자르지 않는다 — 초과분을 그대로 내보내 화면의 한도 경고가 살아 있게 한다(잘라 내면 앞 N개만 조용히 조회된다, moms-fe #739 Critical 2026-09-11). */
export function MultiCodeInput({ value, onChange, placeholder, limit = 1500, disabled = false }: { value: string[]; onChange: (v: string[]) => void; placeholder?: string; limit?: number; disabled?: boolean }) {
  const [open, setOpen] = useState(false)
  // 편집 원문 두 벌(인풋=쉼표·팝오버=줄) — 밖으로 내는 배열만 정규화한다. 입력마다 정규화해 되돌려 쓰면 끝의 구분자가 즉시 지워진다.
  const [text, setText] = useState(value.join('\n'))
  const [line, setLine] = useState(value.join(', '))
  const wrapRef = useRef<HTMLDivElement>(null)
  const joined = value.join('\n')
  const normalize = (raw: string, splitBy: RegExp) => Array.from(new Set(raw.split(splitBy).map((s) => s.trim()).filter(Boolean)))
  const shown = normalize(text, /[\s,]+/)
  // 밖에서 값이 바뀌었을 때만(초기화·복원) 원문을 따라간다 — 내 입력이 만든 값이면 원문(끝의 구분자 포함)을 보존한다.
  const lastEmit = useRef(joined)
  useEffect(() => {
    if (joined === lastEmit.current) return
    lastEmit.current = joined
    setText(joined)
    setLine(value.join(', '))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joined])
  const emit = (arr: string[]) => {
    lastEmit.current = arr.join('\n')
    onChange(arr)
  }
  const onLine = (raw: string) => {
    setLine(raw)
    const arr = normalize(raw, /[\s,]+/)
    setText(arr.join('\n'))
    emit(arr)
  }
  const onText = (raw: string) => {
    setText(raw)
    const arr = normalize(raw, /[\s,]+/)
    setLine(arr.join(', '))
    emit(arr)
  }
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    window.addEventListener('mousedown', onDown)
    return () => window.removeEventListener('mousedown', onDown)
  }, [open])
  return (
    <div className="pbo-filter-code" ref={wrapRef} style={{ position: 'relative', width: '100%' }}>
      <core.Input
        placeholder={placeholder ?? '다건 입력 시, 우측 + 버튼을 눌러주세요.'}
        value={line}
        disabled={disabled}
        onChange={(e) => onLine(e.target.value)}
        suffix={<PlusOutlined style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: 14, cursor: disabled ? 'not-allowed' : 'pointer' }} onMouseDown={(e) => e.preventDefault()} onClick={(e) => { e.stopPropagation(); if (!disabled) setOpen(true) }} />}
      />
      {open && (
        <div className="pbo-filter-code-pop">
          <core.Input.TextArea
            value={text}
            placeholder="입력 후 Enter 키로 다중 입력이 가능합니다."
            onChange={(e) => onText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && shown.length >= limit) e.preventDefault() }}
            autoSize={{ minRows: 4, maxRows: 16 }}
            autoFocus
          />
          <div className="pbo-filter-code-foot">
            <span style={{ fontSize: 12, color: shown.length > limit ? '#ff4d4f' : '#8c8c8c' }}>{shown.length} / {limit.toLocaleString()}</span>
            <core.Button size="small" onClick={() => onText('')}>
              초기화
            </core.Button>
          </div>
        </div>
      )}
    </div>
  )
}

/** 글자 폭 측정 — canvas 는 모듈에 한 번만(렌더마다 createElement 금지, no-return-assign 도 피한다). */
let measureCtx: CanvasRenderingContext2D | null | undefined
function measureTextWidth(t: string, font: string): number {
  if (measureCtx === undefined) measureCtx = document.createElement('canvas').getContext('2d')
  if (!measureCtx) return t.length * 8
  measureCtx.font = font
  return measureCtx.measureText(t).width
}
/** 멀티 셀렉트 — 상단 검색창 + 체크 + 화살표 플립 + 하단 [초기화]. allOption 이면 '' = '전체'(다른 값과 배타). 규격 = .pbo-filter-select* */
export function MultiSelectBox({ value, onChange, options, placeholder, allOption = false, allDisabled = false, className, disabled = false }: { value: string[]; onChange: (v: string[]) => void; options: FilterOption[]; placeholder?: string; allOption?: boolean; allDisabled?: boolean; className?: string; disabled?: boolean }) {
  const [searchQ, setSearchQ] = useState('')
  const [ddOpen, setDdOpen] = useState(false)
  const keep = useRef(false)
  // 보이는 태그 수 = 칸 폭과 글자 폭으로 **직접 계산**(사용자 확정 2026-09-09 "첫 값은 나오고 +n, 넓으면 2개, 안 깨지게").
  //  antd responsive 는 첫 태그조차 안 들어가면 0개로 접고, 값이 바뀔 때마다 재측정하며 깜빡였다(실측). 최소 1개, 나머지는 '+n'.
  const hostRef = useRef<HTMLDivElement>(null)
  const [hostW, setHostW] = useState(0)
  useLayoutEffect(() => {
    const el = hostRef.current
    if (!el) return
    setHostW(el.clientWidth)
    const ro = new ResizeObserver(() => setHostW(el.clientWidth))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  const maxTagCount = (() => {
    if (!hostW || value.length <= 1) return value.length || 1
    const font = hostRef.current ? getComputedStyle(hostRef.current).font || '14px sans-serif' : '14px sans-serif'
    const tw = (t: string) => measureTextWidth(t, font)
    const labelOf = (v: string) => (v === '' ? '전체' : (options.find((o) => o.value === v)?.label ?? v))
    const avail = hostW - 8 - 18 - 4 // 좌우 패딩·화살표·검색 칸
    const MIN_TAG = 56 // 태그가 말줄임으로 줄어들 수 있는 하한(≈3글자 + ×) — 이만큼이라도 들어가면 하나 더 보인다("2개 나오고 말줄임")
    let used = 0
    let n = 0
    for (let i = 0; i < value.length; i++) {
      const w = tw(labelOf(value[i])) + 8 + 16 + 2 // 태그 패딩·닫기 아이콘·간격
      const rest = i + 1 < value.length ? tw(`+${value.length - i - 1}`) + 10 : 0
      if (n > 0 && used + Math.min(w, MIN_TAG) + rest > avail) break
      used += Math.min(w, Math.max(MIN_TAG, avail - used - rest))
      n++
    }
    return Math.max(1, n)
  })()
  const optionsData = [...(allOption ? [{ value: '', label: '전체', disabled: allDisabled }] : []), ...options]
  const emit = (next: string[]) => {
    if (!allOption) return onChange(next)
    if (next.includes('') && !value.includes('')) return onChange([''])
    if (next.length > 1 && next.includes('')) return onChange(next.filter((v) => v !== ''))
    onChange(next)
  }
  return (
    <div ref={hostRef} className={['pbo-filter-select-host', className].filter(Boolean).join(' ')} style={{ width: '100%', minWidth: 0 }}>
    <core.Select placement="bottomLeft" builtinPlacements={POPUP_DOWN}
      className={['pbo-filter-select', 'pbo-filter-select-multi', value.length > 1 ? 'pbo-filter-select-many' : ''].filter(Boolean).join(' ')}
      mode="multiple"
      value={value}
      onChange={emit}
      disabled={disabled}
      showSearch
      allowClear
      placeholder={placeholder}
      options={optionsData}
      optionFilterProp="label"
      style={{ width: '100%' }}
      maxTagCount={maxTagCount}
      maxTagPlaceholder={(omitted) => (omitted.length ? `+${omitted.length}` : null)}
      searchValue={searchQ}
      onSearch={setSearchQ}
      open={ddOpen}
      onFocus={() => setDdOpen(true)}
      onClick={() => setDdOpen(true)}
      onBlur={() => {
        if (keep.current) return
        setDdOpen(false)
        setSearchQ('')
      }}
      suffixIcon={ddOpen ? <UpOutlined style={{ pointerEvents: 'none', color: 'rgba(0, 0, 0, 0.45)' }} /> : <DownOutlined style={{ pointerEvents: 'none', color: 'rgba(0, 0, 0, 0.45)' }} />}
      // eslint-disable-next-line react/no-unstable-nested-components -- antd dropdownRender 는 렌더 prop(컴포넌트 정의 아님)
      dropdownRender={(menu) => (
        <>
          <div style={{ padding: '4px 8px', borderBottom: '1px solid rgba(5, 5, 5, 0.06)' }}>
            <core.Input
              size="small"
              variant="borderless"
              placeholder="검색어 입력"
              prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,0.25)' }} />}
              suffix={searchQ ? <CloseCircleFilled style={{ color: 'rgba(0,0,0,0.25)', cursor: 'pointer' }} onMouseDown={(e) => { e.preventDefault(); setSearchQ('') }} /> : <span />}
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              onMouseDown={() => { keep.current = true }}
              onFocus={() => { keep.current = true }}
              onBlur={() => {
                keep.current = false
                setDdOpen(false)
                setSearchQ('')
              }}
              onKeyDown={(e) => e.stopPropagation()}
            />
          </div>
          {menu}
          <div className="pbo-filter-reset-footer" onMouseDown={(e) => e.preventDefault()}>
            <core.Button size="small" onClick={() => onChange(allOption ? [''] : [])}>
              초기화
            </core.Button>
          </div>
        </>
      )}
    />
    </div>
  )
}

/** 기간 필드 — 레인지피커(프리셋 태그·'오늘/최근 N개월' 푸터·maxRangeDays·periodBase). 값 = ['YYYY-MM-DD','YYYY-MM-DD']. */
export function DateRangeField({ value, onChange, defaultPeriod, periodBase = 'futureTo', allowFuture = false, maxRangeDays, hidePresets, format = 'YY-MM-DD', typeOptions, typeValue, onTypeChange, showTag = true }: { value?: [string, string]; onChange: (v: [string, string] | undefined) => void; defaultPeriod?: string; periodBase?: 'futureTo' | 'pastTo'; allowFuture?: boolean; maxRangeDays?: number; hidePresets?: boolean; format?: string; typeOptions?: FilterOption[]; typeValue?: string | number; onTypeChange?: (v: unknown) => void; /** 프리셋 태그('오늘'·'3개월'·'직접') 표시 — MOSS 는 끈다(어댑터 미러). */ showTag?: boolean }) {
  const TAG: Record<string, string> = { today: '오늘', week: '일주일', month: '1개월', month3: '3개월', month6: '6개월' }
  const def = presetRange(defaultPeriod, periodBase)
  // 값이 비면(진입·초기화) 기본 기간으로 되돌려 보고한다 — 조회 조건에도 기본 기간이 걸린다.
  useEffect(() => {
    if (!value && def) onChange(def)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])
  // 프리셋 태그는 **현재 값**에서 유도한다 — 조회영역을 접었다 펴면 이 컴포넌트가 리마운트되는데, 기본 프리셋으로 초기화하면
  //  실제 기간이 달라도 태그가 '오늘' 로 돌아간다(moms-fe #738 리뷰 2026-09-11). 값이 프리셋과 일치할 때만 태그를 단다.
  const tagOf = (v?: [string, string]): string | null => {
    if (!v) return (defaultPeriod && TAG[defaultPeriod]) ?? null
    const hit = Object.keys(TAG).find((k) => { const r = presetRange(k, periodBase); return !!r && r[0] === v[0] && r[1] === v[1] })
    return hit ? TAG[hit] : null
  }
  const [tag, setTag] = useState<string | null>(() => tagOf(value))
  const [selected, setSelected] = useState(0)
  const [open, setOpen] = useState(false)
  const [epoch, setEpoch] = useState(0)
  const [panel, setPanel] = useState<'date' | 'month' | 'year'>('date')
  // 기준일 셀렉트가 있으면 칸이 좁을 때(260 미만) 셀렉트를 줄인다(인라인 헬퍼 compact 미러).
  const rootRef = useRef<HTMLDivElement>(null)
  const [compact, setCompact] = useState(false)
  useEffect(() => {
    const el = rootRef.current
    if (!el || !typeOptions?.length) return
    const ro = new ResizeObserver(() => setCompact(el.clientWidth > 0 && el.clientWidth < 260))
    ro.observe(el)
    return () => ro.disconnect()
  }, [typeOptions])
  const apply = (next: [Dayjs, Dayjs], label: string, months: number) => {
    onChange([next[0].format('YYYY-MM-DD'), next[1].format('YYYY-MM-DD')])
    setSelected(months)
    setTag(label)
    setOpen(false)
    setEpoch((e) => e + 1)
  }
  const picker = (
    <core.DatePicker.RangePicker placement="bottomLeft" {...({ builtinPlacements: POPUP_DOWN } as object)}
      key={`${epoch}-${compact ? 'c' : 'f'}`}
      className={typeOptions?.length ? 'pbo-filter-picker' : 'pbo-filter-picker pbo-filter-picker-full'}
      allowClear={!def}
      format={compact && typeOptions?.length ? 'MM-DD' : format}
      placeholder={['시작일', '종료일']}
      value={toDayjsPair(value ?? def)}
      open={open}
      onOpenChange={setOpen}
      onPanelChange={(_v, modes) => setPanel(((modes as string[]).find((x) => x !== 'date') as 'month' | 'year' | undefined) ?? 'date')}
      prefix={tag && showTag ? <core.Tag className="pbo-filter-date-tag" color="default" style={{ marginInlineEnd: 4 }}>{tag}</core.Tag> : undefined}
      onChange={(vals) => {
        setSelected(0)
        setTag(vals && vals[0] && vals[1] ? '직접' : null)
        onChange(vals && vals[0] && vals[1] ? [vals[0].format('YYYY-MM-DD'), vals[1].format('YYYY-MM-DD')] : undefined)
      }}
      disabledDate={(current, info) => {
        if (periodBase === 'pastTo' && !allowFuture && current.isAfter(dayjs(), 'day')) return true
        if (maxRangeDays && info?.from && Math.abs(current.diff(info.from, 'days')) > maxRangeDays - 1) return true
        return false
      }}
      renderExtraFooter={
        hidePresets
          ? undefined
          : () => (
              <div className={`pbo-filter-date-footer ${panel}`}>
                <core.Button type="link" onClick={() => apply([dayjs(), dayjs()], '오늘', 0)}>
                  오늘
                </core.Button>
                <div className="pbo-filter-date-presets">
                  <span>{periodBase === 'pastTo' ? '최근' : '오늘부터'}</span>
                  {[1, 2, 3, 6].map((m) => (
                    <core.Button key={m} className={m === selected ? 'selected' : undefined} onClick={() => apply(periodBase === 'pastTo' ? [dayjs().subtract(m, 'month'), dayjs()] : [dayjs(), dayjs().add(m, 'month')], `${m}개월`, m)}>
                      {m}개월
                    </core.Button>
                  ))}
                </div>
              </div>
            )
      }
      style={{ width: '100%' }}
    />
  )
  if (!typeOptions?.length) return picker
  return (
    <div className={`pbo-filter-date pbo-filter-row${compact ? ' pbo-filter-date-compact' : ''}`} ref={rootRef}>
      {/* 기준일 — onTypeChange 가 있으면(typeKey) 조회 조건에 싣는다. 없으면 보이기만 한다. */}
      <core.Select placement="bottomLeft" builtinPlacements={POPUP_DOWN} className="pbo-filter-type" options={typeOptions} {...(onTypeChange ? { value: typeValue ?? typeOptions[0]?.value, onChange: onTypeChange } : { defaultValue: typeOptions[0]?.value })} popupMatchSelectWidth={false} />
      {picker}
    </div>
  )
}

/** 월 범위 필드 — 값 = [시작월 1일, 종료월 말일]. 기본 기간(month3M)은 값이 비면 올려 보고한다 — 조회 조건에도 걸린다
 *  (데모 DateMonthRangeControl 이 마운트·초기화 때 저장소에 올리던 것. 옛 산출물은 표시만 하고 조건엔 안 넣었다). */
export function MonthRangeField({ value, onChange, defaultPeriod }: { value?: [string, string]; onChange: (v: [string, string] | undefined) => void; defaultPeriod?: string }) {
  const def = monthRangeDefault(defaultPeriod)
  useEffect(() => {
    if (!value && def) onChange(def)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])
  return (
    <core.DatePicker.RangePicker placement="bottomLeft" {...({ builtinPlacements: POPUP_DOWN } as object)} className="pbo-filter-picker pbo-filter-picker-full" picker="month" format="YY-MM" placeholder={['시작월', '종료월']}
      value={toDayjsPair(value ?? def)}
      onChange={(v) => onChange(v && v[0] && v[1] ? [v[0].startOf('month').format('YYYY-MM-DD'), v[1].endOf('month').format('YYYY-MM-DD')] : undefined)}
      style={{ width: '100%' }} />
  )
}
const monthRangeDefault = (preset?: string): [string, string] | undefined =>
  preset === 'month3M' ? [dayjs().subtract(3, 'month').startOf('month').format('YYYY-MM-DD'), dayjs().endOf('month').format('YYYY-MM-DD')] : undefined

/** 화주유형 + 화주명 — 두 칸이 한 줄(유형 120 고정은 규격 CSS .pbo-filter-type). 값은 둘 다 문자열 배열. */
export function PairField({ f, typeVal, nameVal, setType, setName, typeDisabled = false, nameDisabled = false }: { f: FilterFieldSpec; typeVal: string[]; nameVal: string[]; setType: (v: string[]) => void; setName: (v: string[]) => void; typeDisabled?: boolean; nameDisabled?: boolean }) {
  const t = f.typeOptions ?? []
  const n = f.nameOptions ?? []
  const nameInit = f.nameAllOption ? [''] : []
  // namePruneToOptions — 화주명 옵션이 바뀌면(유형 변경 → 옵션이 좁힘) 옵션에 없는 화주명을 값에서 뺀다. 다 빠지면 기본값으로(인라인 미러).
  const pruneKey = JSON.stringify(n.map((o) => String(o.value)))
  const setNameRef = useRef(setName)
  setNameRef.current = setName
  const nameValRef = useRef(nameVal)
  nameValRef.current = nameVal
  useEffect(() => {
    if (!f.namePruneToOptions || f.nameMode === 'single' || !n.length) return
    const cur = nameValRef.current
    const kept = cur.filter((v) => v === '' || n.some((o) => String(o.value) === v))
    if (kept.length !== cur.length) setNameRef.current(kept.length ? kept : nameInit)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pruneKey])
  // nameAllDisabledWhenTypeAll — 유형 '전체' 동안 화주명 '전체' 잠금 + 이미 들어 있던 '' 는 뺀다(어댑터 PairControl lockNameAll 미러).
  const lockNameAll = !!f.nameAllDisabledWhenTypeAll && typeVal.includes('')
  useEffect(() => {
    if (lockNameAll && nameValRef.current.includes('')) setNameRef.current(nameValRef.current.filter((x) => x !== ''))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockNameAll])
  // typeChangeClearsName — 유형(single)을 바꾸면 화주명을 비운다(인라인 미러: single 유형 셀렉트에서만).
  const setTypeSingle = (v: unknown) => { setType(v == null ? [] : [v as string]); if (f.typeChangeClearsName) setName([]) }
  return (
    <div className="pbo-filter-row" style={{ width: '100%' }}>
      {f.typeMode === 'single' ? (
        <core.Select placement="bottomLeft" builtinPlacements={POPUP_DOWN} className="pbo-filter-select pbo-filter-type" options={f.typeAllOption ? [{ value: '', label: '전체' }, ...t] : t} placeholder="화주유형" allowClear={!!f.typeAllowClear} value={typeVal[0]} onChange={setTypeSingle} disabled={typeDisabled} />
      ) : (
        <MultiSelectBox className="pbo-filter-type" options={t} allOption={f.typeAllOption} placeholder="화주유형" value={typeVal} onChange={setType} disabled={typeDisabled} />
      )}
      {f.nameMode === 'single' ? (
        <core.Select placement="bottomLeft" builtinPlacements={POPUP_DOWN} className="pbo-filter-select pbo-filter-value" showSearch optionFilterProp="label" options={f.nameAllOption ? [{ value: '', label: '전체' }, ...n] : n} placeholder="화주명" value={nameVal[0]} onChange={(v) => setName(v == null ? [] : [v])} disabled={nameDisabled} />
      ) : (
        <MultiSelectBox className="pbo-filter-value" options={n} allOption={f.nameAllOption} allDisabled={lockNameAll} placeholder="화주명" value={nameVal} onChange={setName} disabled={nameDisabled} />
      )}
    </div>
  )
}
const pairInit = (f: FilterFieldSpec): { type: string[]; name: string[] } => ({
  type: f.typeMode === 'single' ? (f.typeDefault ? [f.typeDefault] : f.typeAllOption ? [''] : []) : f.typeAllOption ? [''] : [],
  name: f.nameAllOption ? [''] : [],
})

// ── 칸 하나: 칩(접힘) 문구 + 컨트롤(열림) ───────────────────────────────────────────────

function chipOf(f: FilterFieldSpec, values: FilterValues, fmt: string): string | null {
  const opts = f.options ?? []
  const v = values[f.key]
  switch (f.control) {
    case 'pair': {
      const init = pairInit(f)
      const tv = (values[f.typeKey ?? ''] as string[] | undefined) ?? init.type
      const nv = (values[f.nameKey ?? ''] as string[] | undefined) ?? init.name
      return chipMulti([...(f.typeOptions ?? []), ...(f.nameOptions ?? [])], [...tv, ...nv], !!f.required)
    }
    case 'code': return chipCodes(v)
    case 'select': return chipSelect(opts, v ?? (f.defaultValue || undefined), !!f.required)
    case 'multiSelect': return chipMulti(opts, v, !!f.required)
    case 'radio': return chipSelect(opts, v ?? f.defaultValue, false)
    case 'date': return f.single ? chipDate(v) : f.monthRange ? chipRange(v ?? monthRangeDefault(f.defaultPeriod), 'YY-MM') : chipRange(v ?? presetRange(f.defaultPeriod, f.periodBase ?? 'futureTo'), fmt)
    default: return chipText(v)
  }
}

function FieldControl({ f, values, setKey, moss, resetToken, locked }: { f: FilterFieldSpec; values: FilterValues; setKey: (key: string) => (next: unknown) => void; moss: boolean; resetToken: number; locked?: Record<string, boolean | undefined> }) {
  const opts = f.options ?? []
  const v = values[f.key]
  const set = setKey(f.key)
  const disabled = !!(f.disabled || locked?.[f.key])
  switch (f.control) {
    case 'pair': {
      const init = pairInit(f)
      const typeKey = f.typeKey ?? `${f.key}.types`
      const nameKey = f.nameKey ?? `${f.key}.names`
      return (
        <PairField f={f}
          typeVal={(values[typeKey] as string[] | undefined) ?? init.type} nameVal={(values[nameKey] as string[] | undefined) ?? init.name}
          setType={setKey(typeKey)} setName={setKey(nameKey)}
          typeDisabled={!!(f.typeDisabled || locked?.[typeKey])} nameDisabled={!!(f.nameDisabled || locked?.[nameKey])} />
      )
    }
    case 'code':
      return <MultiCodeInput value={(v as string[] | undefined) ?? []} onChange={(x) => set(x.length ? x : undefined)} placeholder={f.placeholder} limit={f.limit} disabled={disabled} />
    case 'select': {
      const all = allowAll(f)
      const dflt = f.defaultValue != null && f.defaultValue !== '' ? f.defaultValue : all ? '' : undefined
      return (
        <core.Select placement="bottomLeft" builtinPlacements={POPUP_DOWN} className="pbo-filter-select" showSearch optionFilterProp="label"
          options={all ? [{ value: '', label: '전체' }, ...opts] : opts}
          value={(v as string | undefined) ?? dflt} onChange={(x) => set(x)}
          placeholder={f.placeholder ?? (all ? '전체' : f.label ? `${f.label}을(를) 선택해주세요.` : undefined)}
          disabled={disabled} style={{ width: '100%' }} />
      )
    }
    case 'multiSelect': {
      const all = allowAll(f)
      // 데스크톱은 '전체'([''])로 시작, MOSS 는 빈 칸으로 시작한다(dev store-admin 실측 — 산출물 규칙과 같다).
      const init = all && !moss ? [''] : []
      return <MultiSelectBox options={opts} allOption={all} value={(v as string[] | undefined) ?? init} onChange={(x) => set(x)} placeholder={f.placeholder ?? (f.label ? `${f.label}을(를) 선택해주세요.` : undefined)} disabled={disabled} />
    }
    case 'radio':
      return (
        <div className="pbo-filter-radio">
          <core.Radio.Group options={opts} value={(v as string | undefined) ?? f.defaultValue} onChange={(e) => set(e.target.value)} />
        </div>
      )
    case 'date': {
      const fmt = moss ? 'YYYY-MM-DD' : 'YY-MM-DD'
      if (f.single) {
        return <core.DatePicker placement="bottomLeft" {...({ builtinPlacements: POPUP_DOWN } as object)} format={fmt} placeholder="날짜 선택" allowClear value={v ? dayjs(v as string) : null} onChange={(d) => set(d ? d.format('YYYY-MM-DD') : undefined)} style={{ width: '100%' }} />
      }
      if (f.monthRange) return <MonthRangeField value={v as [string, string] | undefined} onChange={(x) => set(x)} defaultPeriod={f.defaultPeriod} />
      // 초기화 세대(key) — [초기화] 에 태그·프리셋 에폭을 되돌린다(리마운트).
      return <DateRangeField key={resetToken} value={v as [string, string] | undefined} onChange={(x) => set(x)} defaultPeriod={f.defaultPeriod} periodBase={f.periodBase} allowFuture={f.allowFuture} maxRangeDays={f.maxRangeDays} hidePresets={f.hidePresets || moss} format={fmt}
        typeOptions={f.typeOptions} showTag={!moss} {...(f.typeKey ? { typeValue: values[f.typeKey] as string | number | undefined, onTypeChange: setKey(f.typeKey) } : {})} />
    }
    default:
      return <core.Input placeholder={f.placeholder ?? (f.label ? `${f.label} 입력` : undefined)} disabled={disabled} value={(v as string | undefined) ?? ''} onChange={(e) => set(e.target.value)} style={{ width: '100%' }}
        suffix={f.onHelper ? <SearchOutlined style={{ color: '#bfbfbf', fontSize: 12, cursor: 'pointer' }} onClick={() => f.onHelper?.()} /> : undefined} />
  }
}

// ── 그룹 본체 ─────────────────────────────────────────────────────────────────────────

export type FilterCardProps = {
  fields: FilterFieldSpec[]
  /** 조회 조건 — 화면이 소유한다(표 조회가 같은 값을 읽는다). */
  value: FilterValues
  onChange: (next: FilterValues) => void
  /** [조회]. */
  onSearch?: () => void
  /** [초기화] — 화면은 값을 기본값으로 되돌린다. 기간 칸의 태그·프리셋은 이 그룹이 되돌린다. */
  onReset?: () => void
  /** 배치 규약 — 'moss' = 라벨 왼쪽·항상 3열(규격 CSS .pbo-filter-moss). */
  platform?: 'desktop' | 'moss'
  searchText?: string
  resetText?: string
  /** false = [초기화][조회] 없음(조회 버튼이 할 일이 없는 줄 — MOSS 쇼핑백 관리). */
  actions?: boolean
  /** false = 접기 토글 없음. */
  toggle?: boolean
  defaultCollapsed?: boolean
  /** 요약 카드 줄(core.StatCard 들) — 접기 밖이라 접혀도 보인다. 카드 클릭 선택·숫자는 화면이 소유한다. */
  summary?: ReactNode
  /** 요약 카드 아래 안내 문구(summary 가 있을 때만). */
  summaryMessage?: string
  /** 칸 잠금 — 조회 조건 키(pair 는 typeKey/nameKey) → true. 권한으로 화주가 고정된 사용자(dev wiring.filterDisabled 와 같은 모양). moms-fe 15화면. */
  disabledFields?: Record<string, boolean | undefined>
  /** [초기화] 전 확인 — false(또는 false 로 풀리는 Promise)면 초기화하지 않는다(미저장 행 보호, moms-fe gi31·gi35). */
  canReset?: () => boolean | Promise<boolean>
}

/** 조회영역 — 카드 > 접기 스코프 > 칸 격자 + 푸터([필터 닫기]|구분선|[초기화][조회]). 규격 CSS = pbo-core filter-layout.css(.pbo-filter-*). */
export function FilterCard({ fields, value, onChange, onSearch, onReset, platform = 'desktop', searchText = '조회', resetText = '초기화', actions = true, toggle = true, defaultCollapsed = false, summary, summaryMessage, disabledFields, canReset }: FilterCardProps) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed)
  const [resetToken, setResetToken] = useState(0)
  const reset = () => {
    const go = () => { onReset?.(); setResetToken((t) => t + 1) }
    const ok = canReset?.()
    if (ok instanceof Promise) { ok.then((r) => { if (r !== false) go() }, () => undefined); return }
    if (ok !== false) go()
  }
  const moss = platform === 'moss'
  const fmt = moss ? 'YYYY-MM-DD' : 'YY-MM-DD'
  // 갱신은 함수형 하나로 — 기간 칸이 마운트 때 기본 기간을 올리는 것과 다른 칸 입력이 같은 틱에 겹쳐도 서로 덮지 않게.
  const latest = useRef(value)
  latest.current = value
  const setField = (key: string) => (next: unknown) => onChange({ ...latest.current, [key]: next })
  return (
    <core.Card styles={{ body: { padding: '12px 20px' } }} style={{ border: '1px solid #d9d9d9', borderRadius: 8, minWidth: 0 }}>
      <div className="pbo-filter-collapse-open">
        {summary != null && (
          <core.Flex vertical gap={8} align="stretch">
            <core.Flex style={{ overflow: 'auto', minWidth: 0 }} gap={12} align="stretch">
              {summary}
            </core.Flex>
            {summaryMessage ? <core.Typography.Text type="secondary">{summaryMessage}</core.Typography.Text> : null}
            <core.Divider style={{ margin: '8px 0px 0px 0px' }} />
          </core.Flex>
        )}
        <div className={`${collapsed ? 'pbo-filter-collapse-closed' : 'pbo-filter-collapse-open'}${moss ? ' pbo-filter-moss' : ''}`}>
          {collapsed && <span className="pbo-filter-empty-msg">필터가 적용된 항목이 없어요. 필터를 열어 원하는 조건으로 검색해 보세요.</span>}
          <core.Flex gap={20} align="flex-start" wrap>
            {fields.map((f) => (
              <FilterField key={f.key} collapsed={collapsed} label={f.label} required={f.required} tooltip={f.tooltip} width={f.width}
                chip={chipOf(f, value, fmt)} fieldKey={f.fieldId ?? f.key} onOpen={() => setCollapsed(false)}>
                <FieldControl f={f} values={value} setKey={setField} moss={moss} resetToken={resetToken} locked={disabledFields} />
              </FilterField>
            ))}
          </core.Flex>
          {(toggle || actions) && (
            <core.Flex gap={8} justify="flex-end" align="center">
              {toggle && <core.Button icon={collapsed ? <DownOutlined /> : <UpOutlined />} onClick={() => setCollapsed((x) => !x)}>{`필터 ${collapsed ? '열기' : '닫기'}`}</core.Button>}
              {toggle && actions && <span style={{ height: 18, margin: '0 0px', borderLeft: '1px solid #d9d9d9', display: 'block', flex: 'none' }} />}
              {actions && <core.Button onClick={reset}>{resetText}</core.Button>}
              {actions && <core.Button type="primary" onClick={() => onSearch?.()}>{searchText}</core.Button>}
            </core.Flex>
          )}
        </div>
      </div>
    </core.Card>
  )
}
